import { Avatar, Button, Card, Divider, Modal, Tag } from 'antd';
import {
  FaArrowDown,
  FaArrowUp,
  FaCalendarAlt,
  FaChartBar,
  FaCog,
  FaEye,
  FaGift,
  FaHistory,
  FaMoneyBillWave,
  FaPhone,
  FaTimesCircle,
  FaTrophy,
  FaUndo,
  FaUser,
  FaWallet,
} from 'react-icons/fa';

import BetHistory from './BetHistory';
import ModalUpdateWallet from './ModalUpdateWallet';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStateContext } from '~/contexts/ContextProvider';

export default function DetailsReportMobile({ data, getData }) {
  const { formatMoney } = useStateContext();
  const { t } = useTranslation('report');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewHistory = () => {
    navigate('/report/bet-history', {
      state: { data: { username: data.username } },
    });
    setOpen(false);
  };

  // Định nghĩa các mục thông tin với icons và colors
  const infoItems = [
    {
      icon: <FaCalendarAlt className="text-blue-500" />,
      label: t('ngaydangky'),
      value: dayjs(data?.created_at).format('DD/MM/YYYY'),
      color: 'text-gray-700',
    },
    {
      icon: <FaPhone className="text-green-500" />,
      label: t('sdt'),
      value: data?.phone || 'Chưa cập nhật',
      color: 'text-gray-700',
    },
    {
      icon: <FaChartBar className="text-purple-500" />,
      label: t('level'),
      value: data?.level,
      color: 'text-purple-600',
      isTag: true,
    },
    {
      icon: <FaUser className="text-indigo-500" />,
      label: t('hoten'),
      value: data?.fullname || 'Chưa cập nhật',
      color: 'text-gray-700',
    },
  ];

  const financialItems = [
    {
      icon: <FaWallet className="text-emerald-500" />,
      label: t('sodu'),
      value: formatMoney(data?.money),
      color: data?.money >= 0 ? 'text-emerald-600' : 'text-red-600',
      isBalance: true,
    },
    {
      icon: <FaArrowUp className="text-green-500" />,
      label: t('sotiennap'),
      value: formatMoney(data?.totalRecharge),
      color: 'text-green-600',
    },
    {
      icon: <FaArrowDown className="text-red-500" />,
      label: t('sotienrut'),
      value: formatMoney(data?.totalWithdraw),
      color: 'text-red-600',
    },
    {
      icon: <FaCog className="text-orange-500" />,
      label: t('dieuchinhbangtay'),
      value: formatMoney(data?.handEdit),
      color: data?.handEdit >= 0 ? 'text-green-600' : 'text-red-600',
    },
  ];

  const gameItems = [
    {
      icon: <FaMoneyBillWave className="text-blue-500" />,
      label: t('sotiendatcuoc'),
      value: formatMoney(data?.totalBetHistory),
      color: 'text-blue-600',
    },
    {
      icon: <FaTrophy className="text-yellow-500" />,
      label: t('tong thang'),
      value: formatMoney(data?.totalBetWin),
      color: 'text-green-600',
    },
    {
      icon: <FaTimesCircle className="text-red-500" />,
      label: t('tong thua'),
      value: formatMoney(data?.totalBetLose),
      color: 'text-red-600',
    },
  ];

  const bonusItems = [
    {
      icon: <FaUndo className="text-cyan-500" />,
      label: t('hoantra'),
      value: formatMoney(data?.totalRefund),
      color: 'text-cyan-600',
    },
    {
      icon: <FaGift className="text-pink-500" />,
      label: t('khuyenmai'),
      value: formatMoney(data?.totalGiftcode),
      color: 'text-pink-600',
    },
  ];

  return (
    <>
      <Button
        type="primary"
        size="small"
        icon={<FaEye />}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          borderRadius: '8px',
        }}>
        {t('chi tiet')}
      </Button>

      <Modal
        open={open}
        closable={false}
        width={420}
        centered
        footer={null}
        className="modern-details-modal"
        styles={{
          body: { padding: 0 },
          header: { padding: 0, border: 'none' },
        }}>
        <div className="relative">
          {/* Header với gradient */}
          <div
            className="relative p-3 text-white rounded-t-2xl"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar
                  size={48}
                  className="text-lg font-bold text-purple-600 bg-white"
                  style={{ border: '2px solid rgba(255,255,255,0.3)' }}>
                  {data?.username?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <h3 className="mb-1 text-xl font-bold">{data?.username}</h3>
                  <p className="text-sm text-purple-100 opacity-90">
                    {t('chi_tiet_thong_tin_thanh_vien')}
                  </p>
                </div>
              </div>
              <Button
                type="text"
                icon={<FaTimesCircle className="text-white" />}
                onClick={handleClose}
                className="text-white border-none hover:bg-white hover:bg-opacity-20"
                size="large"
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-3 bg-gradient-to-br from-gray-50 to-white">
            {/* Thông tin cơ bản */}
            <Card
              size="small"
              className="mb-4 shadow-sm"
              title={
                <span className="flex items-center gap-2 font-semibold text-gray-700">
                  <FaUser className="text-blue-500" />
                  {t('thong_tin_co_ban')}
                </span>
              }
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <div className="space-y-3">
                {infoItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm text-gray-600">{item.label}:</span>
                    </div>
                    {item.isTag ? (
                      <Tag color="purple" className="font-medium">
                        Level {item.value}
                      </Tag>
                    ) : (
                      <span className={`font-medium ${item.color}`}>{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Thông tin tài chính */}
            <Card
              size="small"
              className="mb-4 shadow-sm"
              title={
                <span className="flex items-center gap-2 font-semibold text-gray-700">
                  <FaWallet className="text-green-500" />
                  {t('thong_tin_tai_chinh')}
                </span>
              }
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <div className="space-y-3">
                {financialItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm text-gray-600">{item.label}:</span>
                    </div>
                    <span className={`font-bold ${item.color} ${item.isBalance ? 'text-lg' : ''}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Thông tin game */}
            <Card
              size="small"
              className="mb-4 shadow-sm"
              title={
                <span className="flex items-center gap-2 font-semibold text-gray-700">
                  <FaChartBar className="text-purple-500" />
                  {t('thong_ke_game')}
                </span>
              }
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <div className="space-y-3">
                {gameItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm text-gray-600">{item.label}:</span>
                    </div>
                    <span className={`font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Khuyến mãi */}
            <Card
              size="small"
              className="mb-6 shadow-sm"
              title={
                <span className="flex items-center gap-2 font-semibold text-gray-700">
                  <FaGift className="text-pink-500" />
                  {t('khuyen_mai_va_hoan_tra')}
                </span>
              }
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <div className="space-y-3">
                {bonusItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm text-gray-600">{item.label}:</span>
                    </div>
                    <span className={`font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                type="default"
                size="large"
                onClick={handleClose}
                className="flex-1 h-12 font-medium rounded-xl"
                style={{ borderColor: '#d1d5db' }}>
                {t('dong')}
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<FaHistory />}
                onClick={handleViewHistory}
                className="flex items-center justify-center flex-1 h-12 gap-2 font-medium rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}>
                {t('lich_su_cuoc')}
              </Button>
            </div>
          </div>
        </div>

        <style jsx>{`
          .modern-details-modal .ant-modal-content {
            border-radius: 16px !important;
            overflow: hidden !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
            padding: 0 !important;
          }

          .modern-details-modal .ant-card-head {
            background: #f9fafb !important;
            border-bottom: 1px solid #e5e7eb !important;
            border-radius: 12px 12px 0 0 !important;
            padding: 12px 16px !important;
          }

          .modern-details-modal .ant-card-body {
            padding: 16px !important;
          }

          .modern-details-modal .ant-card-head-title {
            font-size: 14px !important;
            font-weight: 600 !important;
          }
        `}</style>
      </Modal>
    </>
  );
}
